import { useEffect, useRef } from 'react';

const MAX_SSE_RECONNECT_ATTEMPTS = 20;

/**
 * Shared hook for SSE connections with exponential backoff reconnection.
 *
 * @param url - SSE endpoint URL (null to disable)
 * @param setup - Callback to attach event listeners to the EventSource
 */
export function useReconnectingEventSource(
  url: string | null,
  setup: (es: EventSource) => void,
  onExhausted?: () => void,
) {
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!url) return;

    const connect = () => {
      const es = new EventSource(url, { withCredentials: true });
      eventSourceRef.current = es;

      es.onopen = () => {
        attemptsRef.current = 0;
      };

      setup(es);

      es.onerror = () => {
        if (es.readyState === EventSource.CLOSED) {
          es.close();
          eventSourceRef.current = null;

          attemptsRef.current++;
          if (attemptsRef.current >= MAX_SSE_RECONNECT_ATTEMPTS) {
            console.warn(
              `[SSE] Max reconnect attempts (${MAX_SSE_RECONNECT_ATTEMPTS}) reached for ${url}`,
            );
            onExhausted?.();
            return;
          }

          const delay = Math.min(
            1000 * Math.pow(1.5, attemptsRef.current - 1),
            30000,
          );
          console.warn(
            `[SSE] Connection closed — reconnecting in ${Math.round(delay / 1000)}s (attempt ${attemptsRef.current}/${MAX_SSE_RECONNECT_ATTEMPTS})`,
          );
          reconnectTimerRef.current = setTimeout(connect, delay);
        }
      };
    };

    connect();

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      attemptsRef.current = 0;
    };
  }, [url, setup, onExhausted]);
}
